import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Button, Stack, Alert } from '@mui/material';
import SettingsGroup from './SettingsGroup';
import SaveSettingsDialog from './SaveSettingsDialog';

// ---------------------------------------------------------------------------

export const SETTINGS_GROUPS = [
  {
    title: 'Sampling & Logging',
    description: 'Control how often your sensor takes readings and how data is stored.',
    settings: [
      {
        key: 'samplingInterval',
        label: 'Sampling Interval',
        type: 'select',
        options: [
          { value: 1, label: '1 second' },
          { value: 5, label: '5 seconds' },
          { value: 30, label: '30 seconds' },
          { value: 60, label: '1 minute' },
          { value: 300, label: '5 minutes' },
        ],
      },
      {
        key: 'passiveLogging',
        label: 'Background Logging',
        type: 'switch',
        helperText: 'Continuously record air quality data in the background',
      },
      {
        key: 'maxFileSize',
        label: 'Max File Size',
        type: 'select',
        options: [
          { value: 5, label: '5 MB' },
          { value: 10, label: '10 MB' },
          { value: 25, label: '25 MB' },
        ],
      },
    ],
  },
  {
    title: 'LED & Alerts',
    description: 'Customize the LED indicator on your sensor.',
    settings: [
      {
        key: 'ledMode',
        label: 'LED Mode',
        type: 'select',
        options: [
          { value: 'legacy', label: 'Legacy (blue + cyan flash)' },
          { value: 'auto', label: 'Auto (air quality colors)' },
          { value: 'off', label: 'Off' },
          { value: 'manual', label: 'Manual' },
        ],
      },
      {
        key: 'ledBrightness',
        label: 'LED Brightness',
        type: 'slider',
        min: 0,
        max: 100,
        step: 10,
      },
    ],
  },
  {
    title: 'Connectivity',
    description: 'Manage wireless connections and cloud features.',
    settings: [
      {
        key: 'bleAdvName',
        label: 'Bluetooth Name',
        type: 'text',
        helperText: 'The name your sensor broadcasts over Bluetooth',
      },
      {
        key: 'wifiSSID',
        label: 'WiFi Network',
        type: 'text',
        disabled: true,
        helperText: 'Coming in a future firmware update',
      },
      {
        key: 'cloudSync',
        label: 'Cloud Sync',
        type: 'switch',
        disabled: true,
        helperText: 'Coming in a future firmware update',
      },
    ],
  },
];

export function getSettingLabel(key) {
  for (const group of SETTINGS_GROUPS) {
    for (const setting of group.settings) {
      if (setting.key === key) return setting.label;
    }
  }
  return key;
}

// ---------------------------------------------------------------------------

export default function SettingsTab({
  serialClient,
  deviceConfig,
  setDeviceConfig,
  onError,
  busyOperation,
  setBusyOperation,
}) {
  const [editedConfig, setEditedConfig] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  // Initialize edited config when device config loads
  useEffect(() => {
    if (deviceConfig) {
      setEditedConfig({ ...deviceConfig });
    }
  }, [deviceConfig]);

  const handleChange = useCallback((key, value) => {
    setEditedConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  const changedKeys = useMemo(() => {
    if (!editedConfig || !deviceConfig) return [];
    return Object.keys(editedConfig).filter(
      (key) => editedConfig[key] !== deviceConfig[key]
    );
  }, [editedConfig, deviceConfig]);

  const handleCancel = useCallback(() => {
    setEditedConfig({ ...deviceConfig });
  }, [deviceConfig]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveResult(null);
    setBusyOperation('saving');

    const failedKeys = [];
    for (const key of changedKeys) {
      try {
        await serialClient.set(key, editedConfig[key]);
      } catch (err) {
        failedKeys.push(key);
        onError(`Failed to save ${getSettingLabel(key)}: ${err.message}`);
      }
    }

    setSaving(false);
    setBusyOperation(null);

    if (failedKeys.length === 0) {
      setDeviceConfig({ ...editedConfig });
      setSaveResult({ success: true });
    } else {
      // Update config for keys that succeeded
      const successfulConfig = { ...deviceConfig };
      for (const key of changedKeys) {
        if (!failedKeys.includes(key)) {
          successfulConfig[key] = editedConfig[key];
        }
      }
      setDeviceConfig(successfulConfig);
      setSaveResult({
        success: false,
        error: `Failed to save: ${failedKeys.map(getSettingLabel).join(', ')}`,
      });
    }
  }, [changedKeys, editedConfig, deviceConfig, serialClient, setBusyOperation, setDeviceConfig, onError]);

  const handleCloseDialog = useCallback(() => {
    setShowReviewDialog(false);
    setSaveResult(null);
  }, []);

  // No config available — firmware doesn't support CONFIG command
  if (!deviceConfig) {
    return (
      <Alert severity="info">
        Settings require a firmware update. Connect your sensor and update the firmware to enable
        device configuration.
      </Alert>
    );
  }

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Device Settings
      </Typography>

      {SETTINGS_GROUPS.map((group) => (
        <SettingsGroup
          key={group.title}
          title={group.title}
          description={group.description}
          settings={group.settings}
          values={editedConfig}
          onChange={handleChange}
        />
      ))}

      {/* Sticky footer with unsaved changes */}
      {changedKeys.length > 0 && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            p: 2,
            mt: 2,
            zIndex: 10,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {changedKeys.length} unsaved {changedKeys.length === 1 ? 'change' : 'changes'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button onClick={handleCancel} disabled={!!busyOperation}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => setShowReviewDialog(true)}
                disabled={!!busyOperation}
              >
                Review &amp; Save to Device
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      <SaveSettingsDialog
        open={showReviewDialog}
        onClose={handleCloseDialog}
        onSave={handleSave}
        savedConfig={deviceConfig}
        editedConfig={editedConfig || {}}
        changedKeys={changedKeys}
        saving={saving}
        saveResult={saveResult}
        getSettingLabel={getSettingLabel}
      />
    </>
  );
}

SettingsTab.propTypes = {
  serialClient: PropTypes.object.isRequired,
  deviceConfig: PropTypes.object,
  setDeviceConfig: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  busyOperation: PropTypes.string,
  setBusyOperation: PropTypes.func.isRequired,
};
